/**
 * TimeOffTable Component
 * Displays TimeOff data in a table format
 * Memoized to prevent unnecessary re-renders
 */

import React, { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import DateConverter from "../../helpers/dateFormatChange";
import TruncateUserName from "../../helpers/truncateUserName";
import { calculateNumberOfDays, calculateDaysBetweenDates, formatUserName } from "../utils/timeOffUtils";
import { canViewAllUsers } from "../utils/timeOffUtils";
import profile_image from "../../../images/user/Default_pfp.png";
import TimeOffTableSkeleton from "./TimeOffTableSkeleton";

const TimeOffTable = memo(({
  data = [],
  isLoading = false,
  user,
  onRowClick,
}) => {
  // Memoize expensive calculations
  const showUserColumn = useMemo(() => canViewAllUsers(user?.userType), [user?.userType]);

  if (isLoading && data.length === 0) {
    return (
      <div className="time-table">
        <table style={{ minWidth: "50rem", width: "100%", textAlign: "left" }}>
          <thead>
            <tr>
              {showUserColumn && <th style={{ minWidth: "300px" }}>User Name</th>}
              <th>Type</th>
              <th>Issue Date</th>
              <th>Total Days</th>
              <th>Days Filed Before Leave</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Review By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <TimeOffTableSkeleton rows={10} showUserColumn={showUserColumn} />
          </tbody>
        </table>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="time-table">
        <table style={{ minWidth: "50rem", width: "100%", textAlign: "left" }}>
          <thead>
            <tr>
              {showUserColumn && <th style={{ minWidth: "300px" }}>User Name</th>}
              <th>Type</th>
              <th>Issue Date</th>
              <th>Total Days</th>
              <th>Days Filed Before Leave</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Review By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={showUserColumn ? 10 : 9} style={{ textAlign: "center" }}>
                No Data Available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="time-table">
      <table
        style={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
        className={isLoading ? "loading-fade" : ""}
      >
        <thead>
          <tr>
            {showUserColumn && <th style={{ minWidth: "300px" }}>User Name</th>}
            <th>Type</th>
            <th>Issue Date</th>
            <th>Total Days</th>
            <th>Days Filed Before Leave</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Status</th>
            <th>Review By</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={row._id || index}>
              {showUserColumn && (
                <td>
                  {row.userDetails && (
                    <div className="time0ff-membername">
                      <span className="user-image-listingteam">
                        <img
                          src={
                            row.userDetails.profile_image
                              ? `/uploads/lowProfileImage/${row.userDetails.profile_image}`
                              : "/uploads/lowProfileImage/user_default.png"
                          }
                          alt={row.userDetails.first_name || "User"}
                          className="member-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/uploads/lowProfileImage/user_default.png";
                          }}
                        />
                      </span>
                      <TruncateUserName textContent={formatUserName(row.userDetails)} />
                    </div>
                  )}
                </td>
              )}

              <td>{row.leaveDetails?.leave_type || "N/A"}</td>
              
              <td>
                {row.created_at ? (
                  moment
                    .tz(moment(row.created_at).format("YYYY-MM-DD"), global.config.getTimeZone())
                    .locale("en-gb")
                    .format("MMMM DD, YYYY")
                ) : (
                  "N/A"
                )}
              </td>
              
              <td>
                {calculateNumberOfDays(
                  row.start_date,
                  row.start_time,
                  row.end_date,
                  row.end_time
                )}
              </td>
              
              <td>
                {row.created_at && row.start_date ? (
                  calculateDaysBetweenDates(row.created_at, row.start_date)
                ) : (
                  "N/A"
                )}
              </td>
              
              <td>
                {row.start_date ? <DateConverter date={row.start_date} /> : "N/A"}
              </td>
              
              <td>
                {row.end_date ? <DateConverter date={row.end_date} /> : "N/A"}
              </td>
              
              <td style={{ paddingRight: "30px" }}>
                <div className={row?.status?.toLowerCase()}>{row.status}</div>
              </td>
              
              <td>
                {row.torVerifiedByDetails ? (
                  <div className="time0ff-membername">
                    <span className="user-image-listingteam">
                      {row.torVerifiedByDetails.profile_image !== null &&
                      row.torVerifiedByDetails.profile_image !== "null" ? (
                        <img
                          src={`/uploads/lowProfileImage/${row.torVerifiedByDetails.profile_image}`}
                          alt={row.torVerifiedByDetails.first_name || "User"}
                          className="member-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/uploads/lowProfileImage/user_default.png";
                          }}
                        />
                      ) : (
                        <img
                          src={profile_image}
                          alt={formatUserName(row.torVerifiedByDetails)}
                          className="member-image"
                        />
                      )}
                    </span>
                    <TruncateUserName textContent={formatUserName(row.torVerifiedByDetails)} />
                  </div>
                ) : (
                  <div>Not Verified</div>
                )}
              </td>
              
              <td>
                <Link
                  to={`/user/time-off/view/${row._id}`}
                  className="cta-link"
                >
                  Details
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.data === nextProps.data &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.user?.userType === nextProps.user?.userType &&
    prevProps.onRowClick === nextProps.onRowClick
  );
});

TimeOffTable.displayName = "TimeOffTable";

export default TimeOffTable;
